// Email identity and auth error copy, shared by every Cognito call in auth-funcs.js.
//
// Both user pools match emails case-sensitively and that cannot be changed, so "Jo@x.com" and
// "jo@x.com" are two accounts (QA sweep 2026-10-16). The invariant is that every native account's
// email is trimmed and lowercase: we normalize here, at the one choke point, and the server's
// PreSignUp trigger refuses anything else.
//
// Accounts made before that invariant can still be mixed case until they are migrated. Sign-in and
// sign-up confirmation try the normalized address first and, only when that misses and the user
// typed something different, once more with exactly what they typed (trimmed).

import { NETWORK_RATE_LIMIT_COPY } from '../backendIntegration/apiErrors';

/**
 * Under every box where someone types a code we emailed (sign-up, password reset, email change).
 * Our sending domain is still building its reputation, so codes land in spam often enough that
 * people decide the code never came and give up. The iOS app shows the same line.
 */
export const CODE_SPAM_HINT = "Can't find the code? Check your spam or junk folder. It can take a minute to arrive.";

/** The address as every new account stores it. */
export const normalizeEmail = (value) => String(value ?? '').trim().toLowerCase();

/** What the user typed, trimmed, when it differs from the normalized address; else null. */
export const legacyEmailVariant = (value) => {
  const typed = String(value ?? '').trim();
  return typed && typed !== normalizeEmail(typed) ? typed : null;
};

// With PreventUserExistenceErrors on, an address with no account answers sign-in with
// NotAuthorizedException ("Incorrect username or password.") and ConfirmSignUp with
// ExpiredCodeException, so those are what "no such account" looks like. Too many attempts is also
// a NotAuthorizedException, and retrying it would only add another failed attempt.
const isUnknownUser = (error) =>
  ['NotAuthorizedException', 'UserNotFoundException'].includes(error?.name) &&
  !/attempts exceeded|status is CONFIRMED/i.test(error?.message ?? '');

export const isSignInMiss = isUnknownUser;
export const isConfirmMiss = (error) =>
  isUnknownUser(error) || ['ExpiredCodeException', 'CodeMismatchException'].includes(error?.name);

/**
 * Runs `call(username)` with the normalized address, then once with the legacy spelling when the
 * first attempt was a miss. Resolves to `{ result, username }` (the spelling that answered). A
 * failure carries `error.username` too, so a caller can send the user on with the address Cognito
 * actually knows (for example to the code screen after UserNotConfirmedException).
 */
export const withLegacyEmailRetry = async (typed, call, isMiss) => {
  const primary = normalizeEmail(typed);
  const legacy = legacyEmailVariant(typed);
  try {
    return { result: await call(primary), username: primary };
  } catch (error) {
    if (!legacy || !isMiss(error)) {
      error.username = primary;
      throw error;
    }
  }
  try {
    return { result: await call(legacy), username: legacy };
  } catch (error) {
    error.username = legacy;
    throw error;
  }
};

const PASSWORD_RULE_COPY =
  'Your password needs at least 12 characters, with an uppercase letter, a lowercase letter, a number and a symbol.';

const isNetworkError = (error) =>
  error?.name === 'NetworkError' || /network ?error|failed to fetch|load failed/i.test(error?.message ?? '');

// Kept in backendIntegration/apiErrors (the API's 429 uses it too); re-exported for the auth callers.
export { NETWORK_RATE_LIMIT_COPY };

/**
 * The user pool's WAF (sign-up bot limits) refuses before Cognito runs, with HTTP 403
 * ForbiddenException. Every error Cognito itself answers is a 400, so a 403 is always the WAF.
 * Safe to say on the password reset form too: it is about the network, not the address.
 */
export const isWafBlocked = (error) =>
  error?.name === 'ForbiddenException' ||
  // Amplify's AuthError keeps the status as `metadata`; the raw service error as `$metadata`.
  (error?.metadata ?? error?.$metadata)?.httpStatusCode === 403 ||
  /blocked by AWS WAF/i.test(error?.message ?? '');

/**
 * Plain copy for a Cognito or Amplify auth error. `context` is one of
 * 'signIn' | 'signUp' | 'confirm' | 'resend' | 'reset' | 'changePassword' | 'changeEmail'.
 * Never echoes Cognito's own text, which leaks internals ("username is required to signIn") and,
 * on some calls, whether an account exists.
 */
export const authErrorMessage = (error, context = 'signIn') => {
  const name = error?.name ?? '';
  const message = error?.message ?? '';

  if (isNetworkError(error)) return "We couldn't reach the server. Check your connection and try again.";
  if (isWafBlocked(error)) return NETWORK_RATE_LIMIT_COPY;

  // Cognito adds its own period after the trigger's message, which already ends in one.
  const trigger = /PreSignUp failed with error (.+)$/s.exec(message);
  if (trigger) return trigger[1].trim().replace(/\.{2,}$/, '.');

  if (/^EmptySignIn|^EmptySignUp|^EmptyConfirm|^EmptyReset/.test(name)) {
    return context === 'confirm' || context === 'reset'
      ? 'Enter the code from the email.'
      : 'Enter your email and password.';
  }

  switch (name) {
    case 'UsernameExistsException':
    case 'AliasExistsException':
      return context === 'changeEmail'
        ? 'Another account already uses that email.'
        : 'An account with this email already exists. Sign in instead.';
    case 'InvalidPasswordException':
      return PASSWORD_RULE_COPY;
    case 'NotAuthorizedException':
      if (/attempts exceeded/i.test(message)) return 'Too many failed attempts. Wait a few minutes, or reset your password.';
      if (context === 'changePassword') return 'Your current password is not right.';
      if (context === 'confirm' && /status is CONFIRMED/i.test(message)) return 'This email is already verified. Sign in instead.';
      return 'Incorrect email or password.';
    case 'UserNotFoundException':
      return 'Incorrect email or password.';
    case 'CodeMismatchException':
      return 'That code is not right. Check the email and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired or is not valid. Request a new one.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'InvalidParameterException':
      if (/password/i.test(message)) return PASSWORD_RULE_COPY;
      if (/email/i.test(message)) return 'Enter a valid email address.';
      break;
    default:
      break;
  }
  return 'Something went wrong. Please try again.';
};
